/* globals process */

import pino from 'pino';

const SUPPORTED_LOG_LEVELS = new Set([
  'fatal',
  'error',
  'warn',
  'info',
  'debug',
  'trace',
  'silent',
]);

const DEFAULT_LOG_LEVEL = 'info';

const isError = (value) => value instanceof Error;

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Error);

const normalizeLogLevel = (value) => {
  const normalized = typeof value === 'string' ? value.toLowerCase().trim() : '';
  return SUPPORTED_LOG_LEVELS.has(normalized) ? normalized : DEFAULT_LOG_LEVEL;
};

const resolveLogLevel = () => {
  if (typeof window !== 'undefined') {
    return normalizeLogLevel(window.__CLIENT_ENV__?.LOG_LEVEL);
  }

  if (typeof process !== 'undefined') {
    return normalizeLogLevel(process.env.LOG_LEVEL);
  }

  return DEFAULT_LOG_LEVEL;
};

const isNodeRuntime =
  typeof process !== 'undefined' && Boolean(process.versions && process.versions.node);

const isProduction = isNodeRuntime && process.env.NODE_ENV === 'production';

const baseLogger = pino({
  level: resolveLogLevel(),
  ...(isNodeRuntime && !isProduction
    ? {
        transport: {
          target: 'pino-pretty',
          options: {
            colorize: true,
            ignore: 'pid,hostname',
            translateTime: 'SYS:standard',
          },
        },
      }
    : {}),
});

const writeLog = (level, args) => {
  if (!args.length) {
    return;
  }

  const [first, ...rest] = args;

  if (typeof first === 'string') {
    if (rest.length === 1 && isPlainObject(rest[0])) {
      baseLogger[level](rest[0], first);
      return;
    }

    if (rest.length === 1 && isError(rest[0])) {
      baseLogger[level]({ err: rest[0] }, first);
      return;
    }

    if (!rest.length) {
      baseLogger[level](first);
      return;
    }

    const payload = { args: rest };
    const err = rest.find(isError);
    if (err) {
      payload.err = err;
    }

    baseLogger[level](payload, first);
    return;
  }

  if (isError(first)) {
    if (typeof rest[0] === 'string') {
      baseLogger[level]({ err: first, args: rest.slice(1) }, rest[0]);
      return;
    }

    baseLogger[level]({ err: first, args: rest }, first.message);
    return;
  }

  if (isPlainObject(first)) {
    if (typeof rest[0] === 'string') {
      baseLogger[level](first, rest[0]);
      return;
    }

    if (rest.length) {
      baseLogger[level]({ ...first, args: rest });
      return;
    }

    baseLogger[level](first);
    return;
  }

  if (rest.length) {
    baseLogger[level]({ args: rest }, String(first));
    return;
  }

  baseLogger[level](String(first));
};

export const logger = {
  fatal: (...args) => writeLog('fatal', args),
  error: (...args) => writeLog('error', args),
  warn: (...args) => writeLog('warn', args),
  info: (...args) => writeLog('info', args),
  debug: (...args) => writeLog('debug', args),
  trace: (...args) => writeLog('trace', args),
  log: (...args) => writeLog('info', args),
  child: (bindings = {}) => baseLogger.child(bindings),
  level: baseLogger.level,
};

if (typeof window !== 'undefined') {
  window.__SJP_LOGGER__ = logger;
}

export const getLogLevel = resolveLogLevel;
